import { useState, useEffect } from 'react';

// Using native fetch instead of Octokit to reduce bundle size
const GITHUB_API_BASE = 'https://api.github.com';

export interface GitHubRepo {
  id: number;
  name: string;
  description: string | null;
  html_url: string;
  language: string | null;
  stargazers_count: number;
  forks_count: number;
  topics: string[];
  updated_at: string;
  homepage: string | null;
}

export interface GitHubOrg {
  id: number;
  login: string;
  description: string | null;
  avatar_url: string;
}

const CACHE_KEY = 'github_repos_cache';
const ORGS_CACHE_KEY = 'github_orgs_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface CachedData {
  repos: GitHubRepo[];
  timestamp: number;
}

interface OrgsCachedData {
  orgs: GitHubOrg[];
  timestamp: number;
}

export const useGitHub = (username: string) => {
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRepos = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const cachedData: CachedData = JSON.parse(cached);
          const isExpired = Date.now() - cachedData.timestamp > CACHE_DURATION;

          if (!isExpired) {
            setRepos(cachedData.repos);
            setLoading(false);
            return;
          }
        }

        // Fetch from GitHub API using native fetch (smaller bundle)
        const response = await fetch(
          `${GITHUB_API_BASE}/users/${username}/repos?sort=updated&per_page=100`,
          {
            headers: {
              'Accept': 'application/vnd.github.v3+json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`GitHub API error: ${response.status}`);
        }

        const data = await response.json();

        const formattedRepos: GitHubRepo[] = data.map((repo: any) => ({
          id: repo.id,
          name: repo.name,
          description: repo.description,
          html_url: repo.html_url,
          language: repo.language || null,
          stargazers_count: repo.stargazers_count || 0,
          forks_count: repo.forks_count || 0,
          topics: repo.topics || [],
          updated_at: repo.updated_at || new Date().toISOString(),
          homepage: repo.homepage || null,
        }));

        // Cache the data
        const cacheData: CachedData = {
          repos: formattedRepos,
          timestamp: Date.now(),
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

        setRepos(formattedRepos);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching GitHub repos:', err);
        setError('Failed to fetch repositories');
        setLoading(false);
      }
    };

    fetchRepos();
  }, [username]);

  return { repos, loading, error };
};

// Featured projects (manually curated based on importance)
export const FEATURED_REPOS = [
  'OpenMuse',
  'Personas',
  'ShroudProxy',
  'Lexical-Analyzer',
];

// Filter helper
export const filterReposByLanguage = (repos: GitHubRepo[], language: string): GitHubRepo[] => {
  if (language === 'all') return repos;
  return repos.filter(repo => repo.language?.toLowerCase() === language.toLowerCase());
};

// Get unique languages
export const getUniqueLanguages = (repos: GitHubRepo[]): string[] => {
  const languages = repos
    .map(repo => repo.language)
    .filter((lang): lang is string => lang !== null);
  return Array.from(new Set(languages)).sort();
};

// Format date helper
export const formatLastUpdated = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return 'Updated today';
  if (diffDays === 1) return 'Updated yesterday';
  if (diffDays < 7) return `Updated ${diffDays}d ago`;
  if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `Updated ${Math.floor(diffDays / 30)}mo ago`;
  return `Updated ${Math.floor(diffDays / 365)}y ago`;
};

// Hook to fetch GitHub organizations
export const useGitHubOrgs = (username: string) => {
  const [orgs, setOrgs] = useState<GitHubOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(ORGS_CACHE_KEY);
        if (cached) {
          const cachedData: OrgsCachedData = JSON.parse(cached);
          const isExpired = Date.now() - cachedData.timestamp > CACHE_DURATION;

          if (!isExpired) {
            setOrgs(cachedData.orgs);
            setLoading(false);
            return;
          }
        }

        // Fetch from GitHub API using native fetch (smaller bundle)
        const response = await fetch(
          `${GITHUB_API_BASE}/users/${username}/orgs`,
          {
            headers: {
              'Accept': 'application/vnd.github.v3+json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`GitHub API error: ${response.status}`);
        }

        const data = await response.json();

        const formattedOrgs: GitHubOrg[] = data.map((org: any) => ({
          id: org.id,
          login: org.login,
          description: org.description || null,
          avatar_url: org.avatar_url,
        }));

        // Cache the data
        const cacheData: OrgsCachedData = {
          orgs: formattedOrgs,
          timestamp: Date.now(),
        };
        localStorage.setItem(ORGS_CACHE_KEY, JSON.stringify(cacheData));

        setOrgs(formattedOrgs);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching GitHub orgs:', err);
        setError('Failed to fetch organizations');
        setLoading(false);
      }
    };

    fetchOrgs();
  }, [username]);

  return { orgs, loading, error };
};
